import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { Store, select } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { fromEvent } from 'rxjs/internal/observable/fromEvent'
import { I18nService } from '../i18n.service'
import { TranslationObject } from '../store/i18n.interface'
import { isContentEditableEnabled as isContentEditableEnabledSelector } from '../store/i18n.selector'
import { TranslationUpdateConfirmationComponent } from './translation-update-confirmation/translation-update-confirmation.component'

@Directive({
  selector: '[translationEditableDirective]',
})
export class TranslationEditableDirective implements OnDestroy {
  private _translationKey: string
  private _icon: HTMLImageElement
  private _defaultValue: any

  @Input()
  set translationEditableDirective(key) {
    this._translationKey = key
  }
  @Input()
  set translationEditableDirectiveDefault(defaultValue) {
    this._defaultValue = defaultValue
  }
  private _isEnabled: boolean
  private _inProgress = false
  private _subscriptions: Array<Subscription> = []

  constructor(
    private elRef: ElementRef,
    private renderer2: Renderer2,
    private store: Store<any>,
    private i18nService: I18nService,
    private translateService: TranslateService,
    private matDialog: MatDialog,
  ) {
    this.initIcon()
    this.handleContentEditableToggle()
    this.handleHostClick()
    this.handleEnter()
    this.handleBlur()
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription) => {
      subscription.unsubscribe()
    })
  }

  private submit() {
    this.renderer2.removeClass(this.nativeElement, 'focused-editable')
    const currentTranslation = this.translateService.instant(
      this._translationKey,
      { defaultValue: this._defaultValue },
    )
    /**
     * @description if empty translation submit
     */
    if (!this.textNode) {
      const textNode = document.createTextNode(currentTranslation)
      this.nativeElement.append(textNode)
    } else if (currentTranslation === this.textNode.nodeValue) {
      /**
       * @description if translation is same
       */
      this._inProgress = false
    } else {
      const translation: TranslationObject = {
        key: this._translationKey,
        value: this.textNode.nodeValue,
      }
      const config: MatDialogConfig = {
        data: {
          translation,
          iso2code: this.translateService.store.currentLang,
          defaultValue: this._defaultValue,
        },
      }
      this.matDialog
        .open(TranslationUpdateConfirmationComponent, config)
        .afterClosed()
        .subscribe((restore) => {
          if (restore) {
            const currentTranslation = this.translateService.instant(
              this._translationKey,
              { defaultValue: this._defaultValue },
            )
            this.textNode.nodeValue = currentTranslation
          }
          this._inProgress = false
        })
    }
  }

  private handleBlur() {
    const sub = fromEvent<Event>(this.nativeElement, 'blur').subscribe(() => {
      if (this._isEnabled && !this._inProgress) {
        this.submit()
      }
    })

    this.registrateSubscription(sub)
  }

  private handleEnter() {
    const sub = fromEvent<KeyboardEvent>(
      this.nativeElement,
      'keydown',
    ).subscribe((e) => {
      if (this._isEnabled && e.code === 'Enter') {
        e.preventDefault()
        this._inProgress = true
        this.nativeElement.blur()
        this.submit()
      }
    })

    this.registrateSubscription(sub)
  }

  private handleHostClick() {
    const sub = fromEvent(this.nativeElement, 'click', {
      capture: true,
    }).subscribe((e: Event) => {
      if (this._isEnabled) {
        e.preventDefault()
        e.stopImmediatePropagation()
        this.renderer2.addClass(this.nativeElement, 'focused-editable')
      }
    })

    this.registrateSubscription(sub)
  }

  private handleContentEditableToggle() {
    const sub = this.store
      .pipe(select(isContentEditableEnabledSelector))
      .subscribe((isEnabled) => {
        this._isEnabled = isEnabled
        this.renderer2.setAttribute(
          this.nativeElement,
          'contenteditable',
          isEnabled ? 'true' : 'false',
        )
        this.handleIcon(isEnabled)
      })

    this.registrateSubscription(sub)
  }

  private initIcon() {
    this.renderer2.addClass(this.nativeElement, 'editable')
    this._icon = this.renderer2.createElement('img')
    this.renderer2.addClass(this._icon, 'edit-icon')
    this.renderer2.setAttribute(this._icon, 'src', '/assets/editable.svg')
  }

  private handleIcon(isEnabled) {
    if (isEnabled) {
      this.renderer2.appendChild(this.nativeElement, this._icon)
    } else {
      this.renderer2.removeChild(this.nativeElement, this._icon)
    }
  }

  private registrateSubscription(sub) {
    this._subscriptions.push(sub)
  }

  private get nativeElement(): HTMLElement {
    return this.elRef.nativeElement
  }

  private get textNode() {
    return Array.from(this.nativeElement.childNodes).find(
      (node) => node.nodeType === 3,
    )
  }
}
